import "ol/ol.css";
import "./MapOpenLayer.css";

import { Feature, Map, MapBrowserEvent, Overlay, View } from "ol";
import { defaults as defaultControls } from "ol/control/defaults.js";
import { boundingExtent, createEmpty, extend, isEmpty } from "ol/extent";
import GeoJSON from "ol/format/GeoJSON.js";
import { Geometry } from "ol/geom";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector.js";
import { getUserProjection, toUserResolution, useGeographic } from "ol/proj.js";
import VectorSource from "ol/source/Vector.js";
import XYZ from "ol/source/XYZ";
import Fill from "ol/style/Fill";
import Style from "ol/style/Style";
import { ReactElement, useMemo, useRef, useState } from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";

import { MapboxConstants } from "../../../../constants";
import { logError } from "../../../../service/error";
import { Button, Column, FlyoutPanel, Row } from "../../..";
import { MapConstants } from "../constants";
import HelpControl from "./controls";
import { MapOpenLayerGisFileProps } from "./models";
import { DEFAULT_PROJECT_COLOUR } from "./utils/CommonUtils";
import { createProjectPinLayer } from "./utils/projectPinLayer";

useGeographic();

type BoundaryLayer = VectorLayer<VectorSource<Feature<Geometry>>, Feature<Geometry>>;

/* Keeps a fitted boundary off the zoom and help controls sitting in the map's corners. */
const BOUNDARY_FIT_PADDING = [32, 32, 32, 32];

const BOUNDARY_FIT_DURATION_MS = 300;

/**
 * Inline boundary geometry, or undefined when there is none to be had.
 *
 * `boundaryData` reaches the front end as unvalidated `any` and `readFeatures` throws on anything
 * that is not GeoJSON — including the `"{}"` the unwired service returns. Left to throw it takes the
 * whole location tab down mid-render, so a project with unreadable geometry falls back to its url or,
 * failing that, is skipped like any other project with no boundary.
 */
const readBoundaryFeatures = (geoJsonContent?: string, projectName?: string): Feature<Geometry>[] | undefined => {
  if (!geoJsonContent) return undefined;

  try {
    return new GeoJSON().readFeatures(geoJsonContent);
  } catch (error) {
    logError({
      error: error instanceof Error ? error : `Unreadable boundary data for ${projectName ?? "a project"}`,
    });
    return undefined;
  }
};

interface MapOpenLayerMultipleLayersProps {
  projectCoordinates: { lat: number; lng: number }[];
  gisFiles: MapOpenLayerGisFileProps[];
  drawLayer?: boolean;
  clearDrawBoundaryToggle?: boolean;
  hoverOverlay?: Overlay;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pointermoveFunc?: (event: MapBrowserEvent<any>) => void;
}

export const MapOpenLayerMultipleLayers = ({
  projectCoordinates,
  gisFiles,
  drawLayer,
  clearDrawBoundaryToggle,
  hoverOverlay,
  pointermoveFunc,
}: MapOpenLayerMultipleLayersProps): JSX.Element => {
  const MAPBOX_TOKEN = MapboxConstants.ACCESS_TOKEN;
  const mapElement = useRef<HTMLDivElement | null>(null);
  const [displayHelpPanel, setDisplayHelpPanel] = useState(false);

  const satelliteLayer = useMemo(
    () =>
      new TileLayer({
        source: new XYZ({
          url: `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=${MAPBOX_TOKEN}`,
          tileSize: 512, // Mapbox uses 512px tiles by default
          maxZoom: 19, // Maximum zoom level for satellite tiles
        }),
      }),
    []
  );

  const boundaryLayers = useMemo(() => {
    const tempLayers: BoundaryLayer[] = [];

    gisFiles.forEach((gisFile) => {
      if (!gisFile.projectShape) return;

      const features = readBoundaryFeatures(gisFile.projectShape.geoJsonContent, gisFile.extraInfo?.projectName);

      if (!features && !gisFile.projectShape.geoJsonFileUrl) return;

      const shapeLayer = new VectorLayer({
        source: new VectorSource({
          features: features || undefined,
          url: gisFile.projectShape.geoJsonFileUrl,
          format: new GeoJSON(),
        }),
        opacity: 0.5,
        style: new Style({
          fill: new Fill({
            color: gisFile.projectShape.fillColor || DEFAULT_PROJECT_COLOUR,
          }),
        }),
      });
      shapeLayer.setProperties({
        projectName: gisFile.extraInfo?.projectName,
        areaNetHa: gisFile.extraInfo?.areaNetHa,
        /* Read back by createProjectPinLayer, so a pin always matches the boundary it marks. */
        fillColor: gisFile.projectShape.fillColor,
      });
      tempLayers.push(shapeLayer);
    });

    return tempLayers;
  }, [gisFiles]);

  useDeepCompareEffectNoCheck(() => {
    if (!mapElement.current) return undefined;

    /* Project coordinates are known synchronously, so they can frame the map before any boundary
       geometry exists. Boundaries supplied only as a url load over the network, and without this
       the map would open at a fixed zoom over the average coordinate — leaving every project
       further apart than that viewport off-screen until the refit below. */
    const initialExtent = boundingExtent(projectCoordinates.map(({ lat, lng }) => [lng, lat]));

    /* No centre: the fits below are what define this view. Until one lands the map draws nothing,
       which is the honest answer — a hardcoded fallback location would render a confident picture of
       somewhere the landholding isn't. */
    const view = new View({
      zoom: MapConstants.BOUNDARY_FIT_MAX_ZOOM_LEVEL,
      maxZoom: MapConstants.MAX_ZOOM_LEVEL,
    });

    /* Pins are built here rather than alongside the boundary layers because each one's zoom cutoff
       is measured against this view. Kept last so OpenLayers draws them above every boundary fill. */
    const { pinLayer, dispose: disposePins } = createProjectPinLayer(boundaryLayers, view);
    const layers = [satelliteLayer, ...boundaryLayers, pinLayer];
    const mapObj = new Map({
      layers,
      view,
      target: "map-openlayer-container",
      controls: defaultControls().extend([new HelpControl(setDisplayHelpPanel)]),
    });

    mapObj.setTarget(mapElement.current);

    /* Boundaries can finish loading at any point, so a refit once the user has taken hold of the
       map would pull it out from under them. */
    let userHasInteracted = false;
    const stopFitting = (): void => {
      if (view.isDef()) userHasInteracted = true;
    };

    /* Rebuilt from every source on each call rather than extended in place, so boundaries arriving
       one at a time widen the frame progressively instead of the last to land framing only itself. */
    const fitToBoundaries = (animate: boolean): void => {
      if (userHasInteracted) return;

      const extentOfBoundaries = createEmpty();
      boundaryLayers.forEach((boundaryLayer) => {
        const source = boundaryLayer.getSource();
        /* A source still fetching reports the empty extent, leaving the accumulator untouched. */
        if (source) extend(extentOfBoundaries, source.getExtent());
      });

      if (isEmpty(extentOfBoundaries)) return;

      view.fit(extentOfBoundaries, {
        padding: BOUNDARY_FIT_PADDING,
        maxZoom: MapConstants.BOUNDARY_FIT_MAX_ZOOM_LEVEL,
        duration: animate ? BOUNDARY_FIT_DURATION_MS : undefined,
      });
    };

    /* Fitting needs the map's size, which setTarget above is what establishes. */
    if (!isEmpty(initialExtent)) {
      view.fit(initialExtent, {
        padding: BOUNDARY_FIT_PADDING,
        maxZoom: MapConstants.BOUNDARY_FIT_MAX_ZOOM_LEVEL,
      });
    }
    /* Inline geometry is already parsed, so it supersedes the coordinate frame straight away. */
    fitToBoundaries(false);

    /* Only the boundaries that fetch. A source holding inline features alone keeps OpenLayers' VOID
       loader, which never reports back, so starting one would leave it outstanding forever. */
    const loadingLayers = boundaryLayers.filter((boundaryLayer) => boundaryLayer.getSource()?.getUrl() !== undefined);

    let outstandingLoadCount = loadingLayers.length;
    const onSourceSettled = (): void => {
      outstandingLoadCount -= 1;
      fitToBoundaries(true);

      /* Every boundary has settled and none of them gave the view a centre — with no coordinates
         either there is nothing left to define it, so show the world rather than an empty box. */
      if (outstandingLoadCount === 0 && !view.isDef()) {
        view.setCenter([0, 0]);
        view.setZoom(MapConstants.MIN_ZOOM_LEVEL);
      }
    };

    const sourceListeners = loadingLayers.flatMap((boundaryLayer) => {
      const source = boundaryLayer.getSource();
      if (!source) return [];

      const onLoadEnd = (): void => onSourceSettled();
      const onLoadError = (): void => {
        logError({ error: `Project boundary failed to load for ${boundaryLayer.get("projectName") ?? "a project"}` });
        onSourceSettled();
      };

      source.on("featuresloadend", onLoadEnd);
      source.on("featuresloaderror", onLoadError);

      return [{ source, onLoadEnd, onLoadError }];
    });

    const loadProjection = getUserProjection() ?? view.getProjection();
    const loadResolution = toUserResolution(
      view.getResolutionForZoom(MapConstants.BOUNDARY_FIT_MAX_ZOOM_LEVEL),
      view.getProjection()
    );

    sourceListeners.forEach(({ source }) =>
      source.loadFeatures([-Infinity, -Infinity, Infinity, Infinity], loadResolution, loadProjection)
    );

    /* Listened for on the viewport rather than the map, which only forwards a subset of pointer
       events and no wheel event at all. */
    const viewport = mapObj.getViewport();
    viewport.addEventListener("pointerdown", stopFitting, { passive: true });
    viewport.addEventListener("wheel", stopFitting, { passive: true });

    if (hoverOverlay) mapObj.addOverlay(hoverOverlay);

    mapObj.on("pointermove", (event) => {
      if (pointermoveFunc) pointermoveFunc(event);
    });
    return () => {
      /* The sources outlive this effect — they are memoised on gisFiles alone, while the effect also
         re-runs for the draw toggles — so without this each re-run leaves another listener holding a
         stale view. */
      sourceListeners.forEach(({ source, onLoadEnd, onLoadError }) => {
        source.un("featuresloadend", onLoadEnd);
        source.un("featuresloaderror", onLoadError);
      });
      disposePins();
      viewport.removeEventListener("pointerdown", stopFitting);
      viewport.removeEventListener("wheel", stopFitting);
      mapObj.setTarget("");
    };
  }, [gisFiles, drawLayer, clearDrawBoundaryToggle]);

  const helpContent = (): ReactElement => (
    <div className="map-help-content">
      <div className="map-help-table">
        <Row>
          <Column span={2}>Tool </Column>
          <Column span={10}>Usage </Column>
        </Row>
        <Row>
          <Column span={2}>
            <div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
                <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
                <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
                <path d="M18 11a4 4 0 0 1 4 4v3a8 8 0 0 1-8 8h-4a8 8 0 0 1-8-8v-5a4 4 0 0 1 4-4" />
              </svg>
              Pan map
            </div>
          </Column>
          <Column span={10}>
            <ul>
              <li>Single left click: the map is centred on the clicked point, at the same scale</li>
              <li>Hold down the left mouse button and drag the map canvas</li>
            </ul>
          </Column>
        </Row>
        <Row>
          <Column span={2}>
            <div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
                <path d="M11 8v6M8 11h6" />
              </svg>
              Zoom in
            </div>
          </Column>
          <Column span={10}>
            <ul>
              <li>Single left click: the map is centred on the clicked point, while the scale gets doubled</li>
              <li>Drag a rectangle on the map canvas with the left mouse button to zoom in to an area.</li>
              <li>Hold the Alt key to switch to the &lsquo;Zoom Out&rsquo; tool</li>
            </ul>
          </Column>
        </Row>
        <Row>
          <Column span={2}>
            <div>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
                <path d="M8 11h6" />
              </svg>
              Zoom out
            </div>
          </Column>
          <Column span={10}>
            <ul>
              <li>Single left click: the map is centred on the clicked point, while the scale gets halved</li>
              <li>Drag a rectangle on the map canvas with the left mouse button to zoom out from an area.</li>
              <li>Hold the Alt key to switch to the &lsquo;Zoom In&rsquo; tool.</li>
            </ul>
          </Column>
        </Row>
        <Row>
          <Column span={2}>Mouse wheel</Column>
          <Column span={10}>
            <ul>
              <li>Pan map: Hold and drag the mouse wheel.</li>
              <li>
                Zoom: Roll the mouse wheel to zoom in or zoom out. With Ctrl key pressed while rolling the mouse wheel
                results in a finer zoom.
              </li>
              <li>Press the back or forward button to browse the map canvas zoom history.</li>
            </ul>
          </Column>
        </Row>
        <Button text="Close" className="btn-close" onClick={() => setDisplayHelpPanel(false)} />
      </div>
    </div>
  );

  return (
    <>
      <div id="map-openlayer-container" className="map-container" ref={mapElement} />
      <FlyoutPanel
        show={displayHelpPanel}
        onClose={() => setDisplayHelpPanel(false)}
        size="Large"
        contentChildren={helpContent()}
        header="Map controls"
        buttonsChildren={undefined}
        height="Full"
      />
    </>
  );
};
